
import React from 'react';
import { CopyrightBundleLayout } from '../../components/CopyrightBundleLayout';
import { CheckCircle } from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';

export const CopyrightPSDScreen: React.FC = () => {
  const { t } = useLanguage();
  return (
    <CopyrightBundleLayout>
        <div className="w-full max-w-4xl flex justify-between items-center mb-12">
            <h4 className="text-sm font-bold text-slate-900 uppercase tracking-widest">{t('copyright.psd.reportTitle')}</h4>
            <div className="flex items-center gap-1.5 px-3 py-1 bg-emerald-50 border border-emerald-100 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-tight">{t('copyright.psd.verified')}</span>
            </div>
        </div>

        <div className="w-full max-w-4xl space-y-12">
            <div className="relative flex flex-col items-center py-16 border border-slate-200 rounded-xl bg-slate-50/30">
                <div className="relative">
                    <div className="w-24 h-32 bg-white border-2 border-slate-200 rounded-lg shadow-sm flex items-center justify-center relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-8 h-8 bg-slate-100 border-b border-l border-slate-200" style={{ clipPath: 'polygon(0 0, 100% 100%, 0 100%)', transform: 'rotate(180deg)' }}></div>
                        <span className="text-2xl font-black text-slate-300 tracking-tighter">PSD</span>
                    </div>
                    <div className="absolute -right-16 top-0 bg-white border border-slate-200 px-3 py-1.5 rounded shadow-sm">
                        <p className="text-[10px] font-bold text-primary uppercase">{t('copyright.psd.layers')}</p>
                    </div>
                    <div className="absolute -left-12 top-10 bg-white border border-slate-200 px-3 py-1.5 rounded shadow-sm">
                        <p className="text-[10px] font-bold text-slate-600 uppercase">{t('copyright.psd.dpi')}</p>
                    </div>
                    <div className="absolute -right-20 bottom-8 bg-white border border-slate-200 px-3 py-1.5 rounded shadow-sm">
                        <p className="text-[10px] font-bold text-slate-600 uppercase">{t('copyright.psd.colorProfile')}</p>
                    </div>
                </div>
                <div className="mt-8 text-center">
                    <h3 className="text-lg font-semibold text-slate-800">{t('copyright.psd.fileName')}</h3>
                    <p className="text-xs text-slate-400 font-mono mt-1">UUID: 8832-LW-PS001</p>
                </div>
            </div>

            <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                            <th className="px-6 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest">{t('copyright.psd.colProp')}</th>
                            <th className="px-6 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest">{t('copyright.psd.colValue')}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        <tr>
                            <td className="px-6 py-4">
                                <p className="text-xs font-semibold text-slate-700">{t('copyright.psd.created')}</p>
                                <p className="text-[10px] text-slate-400">{t('copyright.psd.createdDesc')}</p>
                            </td>
                            <td className="px-6 py-4">
                                <p className="text-xs font-mono text-slate-900 bg-slate-50 px-2 py-1 rounded inline-block">2023-10-24T14:22:09.441Z</p>
                            </td>
                        </tr>
                        <tr>
                            <td className="px-6 py-4">
                                <p className="text-xs font-semibold text-slate-700">{t('copyright.psd.fingerprint')}</p>
                                <p className="text-[10px] text-slate-400">{t('copyright.psd.fingerprintDesc')}</p>
                            </td>
                            <td className="px-6 py-4">
                                <p className="text-xs font-mono text-slate-900 break-all leading-tight">f7a93c4e2b10d55e88a31c9003f2a11b668d90e2277c44d189032145e66b801a</p>
                            </td>
                        </tr>
                        <tr>
                            <td className="px-6 py-4">
                                <p className="text-xs font-semibold text-slate-700">{t('copyright.psd.author')}</p>
                                <p className="text-[10px] text-slate-400">{t('copyright.psd.authorDesc')}</p>
                            </td>
                            <td className="px-6 py-4">
                                <p className="text-xs font-mono text-primary font-bold">OCS-USR-VER-48922</p>
                            </td>
                        </tr>
                        <tr>
                            <td className="px-6 py-4">
                                <p className="text-xs font-semibold text-slate-700">{t('copyright.psd.software')}</p>
                                <p className="text-[10px] text-slate-400">{t('copyright.psd.softwareDesc')}</p>
                            </td>
                            <td className="px-6 py-4">
                                <p className="text-xs font-mono text-slate-900">Adobe Photoshop v25.0 (macOS)</p>
                            </td>
                        </tr>
                        <tr>
                            <td className="px-6 py-4">
                                <p className="text-xs font-semibold text-slate-700">{t('copyright.psd.seed')}</p>
                                <p className="text-[10px] text-slate-400">{t('copyright.psd.seedDesc')}</p>
                            </td>
                            <td className="px-6 py-4">
                                <p className="text-xs font-mono text-emerald-600 font-bold italic">OW_8832_LW_CRYPT_ALPHA</p>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="flex justify-between items-center pt-8 border-t border-slate-100">
                <div className="flex items-center gap-4">
                    <div className="flex flex-col">
                        <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{t('copyright.psd.engine')}</span>
                        <span className="text-[11px] font-semibold text-slate-600">DeepScan Forensic v4.2.1</span>
                    </div>
                </div>
                <div className="text-right">
                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{t('copyright.psd.integrity')}</span>
                    <div className="flex items-center gap-1 justify-end">
                        <CheckCircle className="text-emerald-500" fontSize="small" style={{ fontSize: 16 }} />
                        <span className="text-[11px] font-bold text-slate-900">{t('copyright.psd.secure')}</span>
                    </div>
                </div>
            </div>
        </div>
    </CopyrightBundleLayout>
  );
};
